import { Mail, Phone, ShieldCheck } from "lucide-react";

import { LeadCaptureForm } from "@/components/LeadCaptureForm";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { contactDetails } from "@/lib/siteContent";

export const ContactPage = () => {
  return (
    <div>
      <PageHero
        eyebrow="Contact / Request demo"
        title="Start the conversation with a transport provider designed for visibility and safeguarding clarity."
        description="Tell us about your organisation, transport requirements, or current challenges, and we’ll show how CareFleet can support safer, more accountable operations through structured service design and digital control."
        highlights={["Councils", "Schools", "Families", "Specialist transport"]}
        actions={[]}
        baseTestId="contact-hero"
        visual={
          <div className="dashboard-surface p-8">
            <div className="rounded-[28px] border border-white/10 bg-white/5 p-6" data-testid="contact-hero-trust-card">
              <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-2 text-sm font-semibold text-brand-teal">
                <ShieldCheck className="h-4 w-4" />
                Designed for structured accountability
              </div>
              <p className="mt-5 font-heading text-3xl font-semibold text-white">Human care powered by digital control.</p>
              <p className="mt-4 text-base leading-8 text-slate-300">
                We combine specialist transport delivery with systems that help decision makers, school teams, and families see more clearly through structured visibility and controlled handovers.
              </p>
            </div>
          </div>
        }
      />

      <section className="px-4 pb-24 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-8 lg:grid-cols-[0.72fr_1.28fr]">
          <Reveal>
            <div className="space-y-6">
              <div className="soft-card p-8" data-testid="contact-details-card">
                <p className="eyebrow">Contact details</p>
                <div className="mt-6 space-y-6">
                  {contactDetails.map((item) => (
                    <div key={item.label} data-testid={`contact-detail-${item.label.toLowerCase()}`}>
                      <p className="text-sm font-semibold uppercase tracking-[0.18em] text-brand-teal">{item.label}</p>
                      {item.href ? (
                        <a href={item.href} className="mt-2 inline-flex items-center gap-3 text-lg font-medium text-slate-800 hover:text-brand-navy">
                          {item.label === "Email" ? <Mail className="h-5 w-5" /> : <Phone className="h-5 w-5" />}
                          {item.value}
                        </a>
                      ) : (
                        <p className="mt-2 text-base leading-8 text-slate-600">{item.value}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="soft-card p-8" data-testid="contact-expectations-card">
                <p className="eyebrow">What to expect</p>
                <div className="mt-6 space-y-4 text-base leading-8 text-slate-600">
                  <p data-testid="contact-expectation-discovery">A conversation focused on your transport operation, stakeholder needs, and safeguarding priorities.</p>
                  <p data-testid="contact-expectation-platform">A guided view of how CareFleet Guardian supports service visibility and digital accountability.</p>
                  <p data-testid="contact-expectation-next-steps">Practical next steps for councils, schools, or family-facing services.</p>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="soft-card p-8 sm:p-10" data-testid="contact-form-card">
              <Tabs defaultValue="demo" className="space-y-8" data-testid="contact-form-tabs">
                <TabsList className="grid h-auto grid-cols-2 rounded-full bg-brand-mist p-1" data-testid="contact-form-tabs-list">
                  <TabsTrigger value="demo" className="rounded-full py-3" data-testid="contact-demo-tab-trigger">
                    Request Demo
                  </TabsTrigger>
                  <TabsTrigger value="contact" className="rounded-full py-3" data-testid="contact-general-tab-trigger">
                    Contact Form
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="demo" data-testid="contact-demo-tab-content">
                  <LeadCaptureForm
                    enquiryType="demo"
                    eyebrow="Request a demo"
                    title="Tell us what you need."
                    description="Share a few details and we’ll follow up with the most relevant CareFleet demo conversation."
                    submitLabel="Request Demo"
                    successTitle="Demo request saved successfully"
                    successDescription="Thank you for your demo request."
                    testIdPrefix="demo"
                  />
                </TabsContent>

                <TabsContent value="contact" data-testid="contact-general-tab-content">
                  <LeadCaptureForm
                    enquiryType="contact"
                    eyebrow="General contact"
                    title="Send a direct enquiry"
                    description="Use this form for general questions, procurement discussions, or follow-up contact from the CareFleet team."
                    submitLabel="Send Contact Enquiry"
                    successTitle="Contact enquiry saved successfully"
                    successDescription="Thank you for getting in touch."
                    testIdPrefix="contact"
                  />
                </TabsContent>
              </Tabs>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};